import {
  Component,
  computed,
  effect,
  input,
  output,
  signal,
} from '@angular/core';
import {
  FormControl,
  FormGroup,
  FormsModule,
  ReactiveFormsModule,
  Validators,
} from '@angular/forms';
import { TranslateModule, TranslateService } from '@ngx-translate/core';
import { HeadlessService } from '@src/app/core/services/headless/headless.service';
import { WebformService } from '@src/app/core/services/webform/webform.service';
import { CompanyDetails } from '@src/app/core/types/company.type';
import { WebformRequestChangeGeneralData } from '@src/app/core/types/webform.type';
import { ImportsModule } from '@src/app/imports';
import { EcoInputTextComponent } from '@src/app/shared/forms/eco-input-text/eco-input-text.component';
import { MessageService } from 'primeng/api';
import { ButtonModule } from 'primeng/button';
import { InputTextModule } from 'primeng/inputtext';
import { finalize } from 'rxjs';

@Component({
  selector: 'app-amd-company-data',
  imports: [
    TranslateModule,
    ButtonModule,
    InputTextModule,
    FormsModule,
    ReactiveFormsModule,
    EcoInputTextComponent,
    ImportsModule,
  ],
  templateUrl: './amd-company-data.component.html',
  styleUrl: './amd-company-data.component.scss',
})
export class AmdCompanyDataComponent {
  mode = signal<'edit' | 'detail'>('detail');
  visible = input<boolean>(false);
  modeOutput = output<'edit' | 'detail'>({ alias: 'mode' });
  title = computed(
    () =>
      `accountManagement.dialog.companyData.${
        this.isDetail() ? 'title' : 'editTitle'
      }`,
  );
  isDetail = computed(() => this.mode() === 'detail');
  isEdit = computed(() => this.mode() === 'edit');
  companyData = signal<CompanyDetails | undefined>(undefined);
  loadingCompanyData = signal(false);
  savingCompanyData = signal(false);

  form = this.createForm();

  constructor(
    private headlessService: HeadlessService,
    private webformService: WebformService,
    private messageService: MessageService,
    private translateService: TranslateService,
  ) {
    effect(() => {
      this.modeOutput.emit(this.mode());
    });
    effect(() => {
      if (this.visible()) {
        this.loadCompanyData();
      }
    });
  }

  loadCompanyData() {
    this.loadingCompanyData.set(true);
    this.headlessService
      .getDatosEmpresas()
      .pipe(finalize(() => this.loadingCompanyData.set(false)))
      .subscribe((data) => {
        this.companyData.set(data);
        this.populateFormWithCompanyData(this.companyData());
      });
  }

  private populateFormWithCompanyData(companyData: CompanyDetails | undefined) {
    this.form.setValue({
      RAZONSOCIAL: companyData?.RAZONSOCIAL ?? null,
      MAIL: companyData?.MAIL ?? null,
      CIF: companyData?.CIF ?? null,
      TELEFONO: companyData?.TELEFONO ?? null,
      NUMPRODUCTOR: companyData?.NUMPRODUCTOR ?? null,
      WEB: companyData?.WEB ?? null,
      GRUPOCLIENTE: companyData?.GRUPOCLIENTE ?? null,
    });
  }

  createForm() {
    return new FormGroup({
      RAZONSOCIAL: new FormControl('', Validators.required),
      MAIL: new FormControl('', [Validators.required, Validators.email]),
      CIF: new FormControl('', Validators.required),
      TELEFONO: new FormControl(''),
      NUMPRODUCTOR: new FormControl(''),
      WEB: new FormControl(''),
      GRUPOCLIENTE: new FormControl(''),
    });
  }

  editCompanyData() {
    // Sincroniza el formulario con los datos actuales
    this.populateFormWithCompanyData(this.companyData());
    this.mode.set('edit');
  }

  cancelEdit() {
    this.mode.set('detail');
  }

  saveCompanyData() {
    const value = this.form.getRawValue();
    const webformRequest: Partial<WebformRequestChangeGeneralData> = {
      razon_social: value.RAZONSOCIAL ?? '',
      mail: value.MAIL ?? '',
      cif: value.CIF ?? '',
      telefono: value.TELEFONO ?? '',
      numero_de_producto_de_productores: value.NUMPRODUCTOR ?? '',
      web: value.WEB ?? '',
      grupo_empresarial: value.GRUPOCLIENTE ?? '',
    };

    this.savingCompanyData.set(true);
    this.webformService
      .updateCompanyData(webformRequest)
      .pipe(finalize(() => this.savingCompanyData.set(false)))
      .subscribe((data) => {
        if (data.sid) {
          this.messageService.add({
            severity: 'success',
            summary: this.translateService.instant(
              'accountManagement.dialog.companyData.toast.edit.summary',
            ),
            detail: this.translateService.instant(
              'accountManagement.dialog.companyData.toast.edit.detail',
            ),
          });

          this.loadCompanyData();
          this.mode.set('detail');
        }
      });
  }
}
